/**
 * The label this device is remembered by in Tournament Configuration's App Access column.
 *
 * `07-configuration.md` prints it as `iPhone · Paired 14 Sep`, so what is wanted is a
 * **family name a person would recognise on a list**, and nothing more. This is deliberately
 * NOT a fingerprint: no version, no screen metrics, no language, no entropy of any kind. The
 * TD needs to tell "the phone" from "the laptop" when deciding which of the two permitted
 * bindings to revoke (Q-09b: exactly two, one phone and one laptop), and that is the entire
 * requirement.
 *
 * An unrecognised browser gets `Unnamed device` — the same string the API defaults to — in
 * preference to a guess. A wrong label on a revoke list is worse than an honest blank one.
 */

const FAMILIES: readonly { pattern: RegExp; label: string }[] = [
  { pattern: /iPhone/, label: "iPhone" },
  { pattern: /iPad/, label: "iPad" },
  { pattern: /Android/, label: "Android phone" },
  // Modern iPads report as Macintosh; a touch-capable "Mac" is one of those, not a laptop.
  { pattern: /Macintosh|Mac OS X/, label: "Mac laptop" },
  { pattern: /Windows/, label: "Windows laptop" },
  { pattern: /Linux|X11/, label: "Linux computer" },
];

export const UNNAMED_DEVICE = "Unnamed device";

export function deviceLabel(userAgent: string = window.navigator.userAgent): string {
  const touchMac = /Macintosh/.test(userAgent) && window.navigator.maxTouchPoints > 1;
  if (touchMac) return "iPad";
  return FAMILIES.find((family) => family.pattern.test(userAgent))?.label ?? UNNAMED_DEVICE;
}
